//@flow
import moment from "moment"
import momentTz, { tz } from "moment-timezone"
import {
  curry,
  unless,
  compose,
  multiply,
  __,
  divide,
  equals,
  when,
  length,
  toString,
  pluck,
  any,
  startsWith,
  range,
} from "ramda"
import { localized } from "js/includes/common/utils/ssrAndWebUtils"

const toMilliseconds = multiply(1000)
const toSeconds = divide(__, 1000)
const seconds = equals(10)
const getDecimalLength = compose(length, toString, Math.trunc)
const inSeconds = compose(seconds, getDecimalLength)
const format = when(inSeconds, toMilliseconds)
const formatMoment = compose(momentTz, format)
const localTime = (t: number): momentTz => formatMoment(t).tz(momentTz.tz.guess())
const localeData = () => moment().localeData()

export const dateToFormat = curry((outputFormat: string, date: number): string =>
  compose(
    m => (m.isValid() ? m.format(outputFormat) : ""),
    unless(momentTz.isMoment, compose(localTime, Math.round)),
  )(date),
)

export const time = dateToFormat("LT")
export const date = dateToFormat("L")
export const dateTime = dateToFormat("L LT")
export const dateTimeTz = dateToFormat("L LT z")
export const dateTimeLong = dateToFormat("lll")
export const dayDateTime = dateToFormat("llll")
export const dateUtc = (date: number, outputFormat: string) =>
  momentTz
    .unix(date)
    .utc()
    .format(outputFormat)

export const localeIsMeridian = () =>
  localeData()
    ._longDateFormat.LT.toLowerCase()
    .includes("a")
export const calendarTime = (t: number, showDateTimeForDefaultCases: boolean): momentTz => {
  const formatParams = showDateTimeForDefaultCases
    ? {
        sameElse: "L LT",
      }
    : {}
  return formatMoment(t).calendar(formatParams)
}
export const livestamp = (t: number): string => momentTz(format(t)).fromNow()
export const dateSince = (t: number, unit: string): string => {
  const duration = moment.duration(t, unit)
  const days = Math.floor(duration.asDays())
  const hours = duration.hours()
  const minutes = duration.minutes()
  const seconds = duration.seconds()

  const time = [
    duration_string(days, localized("general.day"), localized("general.days")),
    duration_string(hours, localized("general.hour"), localized("general.hours")),
    duration_string(minutes, localized("general.minute"), localized("general.minutes")),
    ...(!minutes ? [duration_string(seconds, localized("general.second"), localized("general.seconds"))] : []),
  ]

  return moment.isDuration(duration) ? time.join(" ") : ""
}

const duration_string = (unit, singular, plural) => {
  if (!unit) return null

  return unit > 1 ? `${unit} ${plural}` : `${unit} ${singular}`
}

export function getTimeFromSplit(timeHours, timeMinutes, format) {
  const minutes = timeMinutes.toString().length === 1 ? `0${timeMinutes}` : timeMinutes
  return moment(`${timeHours}${minutes}`, "hmm").format(format)
}

export const timeZone = () =>
  moment()
    .tz(window.application.get("timeZone").get("value"))
    .format("z")

export const getDivisionTimeZone = () => window.application.get("timeZone").get("value")

export const getDivisionTimeZoneAbbreviation = () => {
  if (getDivisionTimeZone() === "_Local") {
    return localized("general.localDeviceTime")
  }
  const abbreviation = timeZone()
  return `${any(startsWith(__, abbreviation))(["+", "-"]) ? "UTC " : ""}${abbreviation}`
}

export const monthDayYearDateFormat = "MM/DD/YYYY"
export const serverScheduleDateFormat = "YYYY-MM-DD"
export const dateTimeFormat = "MM/DD/YYYY hh:mm A"

export const getDateTimeLocale = datetime => moment(datetime, "L LT")
export const getDateLocale = datetime => moment(datetime, "L")
export const getBrowserTimeZoneReadable = () => tz(tz.guess()).format("z")
export const getSeconds = milliseconds => toSeconds(milliseconds)

export const periodAsText = (amount, unit) => {
  let modifier = ""
  switch (unit) {
    case "second":
      modifier = localized("general.secondsText")
      break
    case "minute":
      modifier = localized("general.minutesText")
      break
    case "hour":
      modifier = localized("general.hoursText")
      break
    case "day":
      modifier = localized("general.daysText")
      break
    case "week":
      modifier = localized("general.weeksText")
      break
    case "month":
      modifier = localized("general.monthsText")
      break
    case "year":
      modifier = localized("general.yearsText")
      break
    default:
      break
  }
  return unit && amount ? ` ${localized("general.forAtLeastText")} ${amount} ${modifier}` : ""
}

export const weekDayList = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"]

export const shortDaysTokensMapper = {
  SUN: "general.sunday",
  MON: "general.monday",
  TUE: "general.tuesday",
  WED: "general.wednesday",
  THU: "general.thursday",
  FRI: "general.friday",
  SAT: "general.saturday",
}

export const weekDaysOptions = () => [
  { id: "SUN", value: localized("general.sunday") },
  { id: "MON", value: localized("general.monday") },
  { id: "TUE", value: localized("general.tuesday") },
  { id: "WED", value: localized("general.wednesday") },
  { id: "THU", value: localized("general.thursday") },
  { id: "FRI", value: localized("general.friday") },
  { id: "SAT", value: localized("general.saturday") },
]

export const weekNumberOptions = [
  { value: "1", labelToken: "editors.common.firstWeek" },
  { value: "2", labelToken: "editors.common.secondWeek" },
  { value: "3", labelToken: "editors.common.thirdWeek" },
  { value: "4", labelToken: "editors.common.fourthWeek" },
  { value: "5", labelToken: "editors.common.fifthWeek" },
]

export const periodOptions = [
  { value: "DAILY", labelToken: "general.daily" },
  { value: "WEEKLY", labelToken: "general.weekly" },
  { value: "MONTHLY", labelToken: "editors.common.monthlyDayOfMonth" },
  { value: "MONTHLY_WEEKNUMBER", labelToken: "editors.common.monthlyDayOfWeek" },
]

export const dayOfMonthOptions = () => [
  ...range(1, 32).map(num => ({ value: num.toString(), labelText: num.toString() })),
  { value: "0", labelToken: "general.lastDay" },
]

export const getWeekDaysOptionsIds = () => pluck("id", weekDaysOptions())

export const isSameDay = (day1, day2) => moment(date(day1)).isSame(date(day2))

export const isBetweenDates = (dateToCheck, from, to, inclusive = false, scope = "day") => {
  const day = formatMoment(dateToCheck)
  const inclusivityToken = inclusive ? "[]" : "()"

  return day.isBetween(from, to, scope, inclusivityToken)
}

export const dateDifference = (date1, date2, unit) => {
  const a = moment(date1)
  const b = moment(date2)
  return b.diff(a, unit)
}

const secondsToDurationUnitFormat = unit => (unit < 10 ? `0${unit}` : unit)
export const secondsToDuration = seconds => {
  const duration = moment.duration(seconds, "seconds")
  const days = Math.floor(duration.asDays())
  const hours = duration.hours()
  const minutes = duration.minutes()

  return {
    days: secondsToDurationUnitFormat(days),
    hours: secondsToDurationUnitFormat(hours),
    minutes: secondsToDurationUnitFormat(minutes),
  }
}

export const getReadableMonTime = monTime => {
  const adjustedMonTime = moment(new Date(monTime * 1000))
  return adjustedMonTime.format("hh:mm A")
}

export const hoursToSeconds = hours => 3600 * hours

export const isDateInFuture = date => moment(date).isAfter(moment())

export const getPreviousDay = (daysBack = 1) => moment().subtract(daysBack, "day")
